import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Loader2, RotateCcw, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { confirmAsync } from "@/lib/safeConfirm";
import { bulletsToText, textToBullets } from "@/lib/infoBlocks";
import { DEFAULT_HOTEL_PERKS, HOTEL_PERKS_TITLE, resolveHotelPerks } from "@/lib/hotelPerks";

// The perk list every hotel card prints on the client page. It lives once
// per trip (not per hotel) and inherits the agency template when the trip
// has nothing of its own (2026-06-16).
export default function HotelPerksEditor({ perks, onChange, testid = "hotel-perks" }) {
  const { user } = useAuth();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingTpl, setSavingTpl] = useState(false);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    api.get("/settings/hotel-perks")
      .then(({ data }) => setTemplate(data))
      .catch(() => setTemplate({ perks: [], is_set: false }))
      .finally(() => setLoading(false));
  }, []);

  const isOwn = Array.isArray(perks);
  const effective = useMemo(() => resolveHotelPerks(perks, template), [perks, template]);
  const text = draft ?? bulletsToText(effective);
  const list = textToBullets(text);

  const onText = (value) => {
    setDraft(value);
    onChange(textToBullets(value));
  };

  const resetToTemplate = async () => {
    if (!(await confirmAsync(
      "¿Descartar las ventajas propias de este viaje y volver a la plantilla de la agencia?",
      { confirmLabel: "Volver" },
    ))) return;
    setDraft(null);
    onChange(null);
    toast.success("Ventajas heredadas de la plantilla");
  };

  const resetToDefaults = () => {
    setDraft(bulletsToText(DEFAULT_HOTEL_PERKS));
    onChange([...DEFAULT_HOTEL_PERKS]);
  };

  const saveAsTemplate = async () => {
    if (!(await confirmAsync(
      "Estas ventajas pasarán a ser la plantilla de la agencia y se usarán en todos los viajes que no tengan las suyas. ¿Seguir?",
      { confirmLabel: "Guardar plantilla" },
    ))) return;
    setSavingTpl(true);
    try {
      const { data } = await api.put("/settings/hotel-perks", { perks: list });
      setTemplate({ ...template, perks: data.perks, is_set: true });
      toast.success("Plantilla de ventajas actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la plantilla");
    } finally { setSavingTpl(false); }
  };

  if (loading) {
    return (
      <div className="mt-6 text-sm text-clay-700 inline-flex items-center gap-2" data-testid={`${testid}-loading`}>
        <Loader2 size={14} className="animate-spin"/> Cargando ventajas…
      </div>
    );
  }

  return (
    <div className="mt-6 border border-clay-300 bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-clay-200 bg-clay-50">
        <Sparkles size={14} className="text-terracotta shrink-0"/>
        <div className="text-[11px] tracking-widest uppercase text-clay-600">
          Ventajas del cliente · «{HOTEL_PERKS_TITLE}»
        </div>
        <span
          className="ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 bg-clay-200 text-clay-700"
          data-testid={`${testid}-origin`}
        >
          {isOwn ? "propias de este viaje" : template?.is_set ? "plantilla de la agencia" : "por defecto"}
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-5 p-4">
        <div>
          <textarea
            value={text}
            onChange={(e) => onText(e.target.value)}
            rows={Math.max(4, text.split("\n").length + 1)}
            placeholder="Una ventaja por línea…"
            data-testid={`${testid}-textarea`}
            className="w-full border border-clay-300 px-3 py-2 text-[13.5px] leading-relaxed outline-none resize-y focus:ring-1 focus:ring-terracotta"
          />
          <div className="text-[11px] text-clay-600 mt-1.5">
            Una ventaja por línea. Se muestra en TODOS los hoteles de este viaje,
            en inglés (lo lee el cliente).
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {isOwn && (
              <button
                onClick={resetToTemplate}
                data-testid={`${testid}-reset-template`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-clay-300 text-[13px] hover:bg-clay-100"
              >
                <RotateCcw size={13}/> Volver a la plantilla
              </button>
            )}
            <button
              onClick={resetToDefaults}
              data-testid={`${testid}-reset-defaults`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-clay-300 text-[13px] hover:bg-clay-100"
            >
              <RotateCcw size={13}/> Texto por defecto
            </button>
            {user?.role === "admin" && (
              <button
                onClick={saveAsTemplate}
                disabled={savingTpl}
                data-testid={`${testid}-save-template`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-clay-900 text-white text-[13px] tracking-wider uppercase hover:bg-terracotta disabled:opacity-50"
              >
                {savingTpl ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>} Guardar como plantilla
              </button>
            )}
          </div>
          {template?.updated_at && (
            <div className="text-[11px] text-clay-600 mt-2">
              Plantilla actualizada por {template.updated_by} · {new Date(template.updated_at).toLocaleDateString("es-ES")}
            </div>
          )}
        </div>

        <div data-testid={`${testid}-preview`}>
          <div className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: "#B08749" }}>
            {HOTEL_PERKS_TITLE}
          </div>
          {list.length === 0 ? (
            <div className="text-[13px] text-clay-600">
              Sin ventajas: esta sección no aparecerá en la ficha de los hoteles.
            </div>
          ) : (
            <ul className="text-[13px] space-y-1.5 leading-relaxed text-clay-800">
              {list.map((line, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 size={13} className="text-emerald-700 mt-0.5 shrink-0"/>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
